import {
  Button,
  FormFeedback,
  Input,
  Modal,
  TextArea,
  useToast
} from '@morressier/alembic';
import { isAxiosError } from 'axios';

import { Flex, Text } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { useMutation } from '@tanstack/react-query';
import {
  addEmailCategory,
  ICreateTemplateCategoryPayload,
  updateEmailCategory
} from 'src/api/email-service';
import {
  ApiValidationError,
  TemplateCategoryDoc
} from '@publishing-engine/domain/models/email-templates';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { queryClient } from 'src/utils/queryClient';
interface Props {
  onClose: () => void;
  isOpen: boolean;
  organizationId: string;
  onSuccess: () => void;
  isCreating?: boolean;
  selectedCategory?: TemplateCategoryDoc | null;
}

const categorySchema = z.object({
  name: z.string().trim().min(1, { message: 'Email Tag is required' }),
  description: z
    .string()
    .max(100, {
      message: 'Description character length should be less than 100'
    })
    .optional()
});

const isApiValidationErrorArray = (
  data: unknown
): data is ApiValidationError[] =>
  Array.isArray(data) &&
  data.length > 0 &&
  data.every(
    item =>
      typeof item === 'object' &&
      item !== null &&
      'message' in item &&
      typeof (item as ApiValidationError).message === 'string'
  );

const getValidationErrors = (
  responseData: unknown
): ApiValidationError[] | null => {
  if (
    typeof responseData === 'object' &&
    responseData !== null &&
    'errors' in responseData &&
    isApiValidationErrorArray((responseData as { errors: unknown }).errors)
  ) {
    return (responseData as { errors: ApiValidationError[] }).errors;
  }

  if (isApiValidationErrorArray(responseData)) {
    return responseData;
  }

  return null;
};

export const getErrorTitle = (
  isFormCreation: boolean,
  error: unknown
): string => {
  if (isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData === 'string') return responseData;

    const validationErrors = getValidationErrors(responseData);
    if (validationErrors) {
      return validationErrors.map(item => item.message).join(', ');
    }

    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      'message' in responseData &&
      typeof responseData.message === 'string'
    ) {
      return responseData.message;
    }
  }

  return isFormCreation
    ? 'Failed to create Category'
    : 'Failed to update Category';
};

const EditCategoryModal = ({
  onClose,
  isOpen,
  organizationId,
  onSuccess,
  isCreating = false,
  selectedCategory
}: Props) => {
  const defaultValue: ICreateTemplateCategoryPayload = selectedCategory
    ? {
        name: selectedCategory.name,
        description: selectedCategory.description
      }
    : {
        name: '',
        description: ''
      };
  const { register, handleSubmit, formState, watch } =
    useForm<ICreateTemplateCategoryPayload>({
      defaultValues: defaultValue,
      resolver: zodResolver(categorySchema)
    });
  const { errors } = formState;
  const toast = useToast();
  const editCategoryMutation = useMutation({
    mutationFn: (data: ICreateTemplateCategoryPayload) =>
      !isCreating && selectedCategory
        ? updateEmailCategory(selectedCategory._id, organizationId, data)
        : addEmailCategory(organizationId, data),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: isCreating
          ? 'Email Tag created successfully'
          : 'Email Tag updated successfully',
        duration: 3000,
        isClosable: true
      });
      queryClient.invalidateQueries({
        queryKey: ['email-categories', { organizationId }]
      });
      onSuccess();
      onClose();
    },
    onError: error => {
      const title = getErrorTitle(isCreating, error);
      toast({
        variant: 'error',
        title,
        duration: 5000,
        isClosable: true
      });
    }
  });

  const onSubmit = (data: ICreateTemplateCategoryPayload) => {
    editCategoryMutation.mutate(data);
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} width="720px">
      <Flex backgroundColor="surfaceSecondaryFill" flexDirection="column">
        <Flex flexDirection="column" margin={6} gap={5}>
          <Flex flexDirection="column" gap={4}>
            <Text textStyle="headingSection">
              {isCreating ? 'Create New Email Tag' : 'Edit Email Tag'}
            </Text>
          </Flex>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex
              flexDirection="column"
              gap={6}
              bgColor="surfacePrimaryFill"
              padding={4}
            >
              <Flex flexDirection="column" gap={3}>
                <FieldLabel label="Email Tag name" subText="" isRequired />
                <Input
                  {...register('name', {
                    required: 'Email Tag name'
                  })}
                  placeholder="Enter email tag name"
                />
                {errors.name && (
                  <FormFeedback variant="error">
                    {errors.name.message}
                  </FormFeedback>
                )}
              </Flex>

              <Flex flexDirection="column" gap={3}>
                <FieldLabel
                  label="Description"
                  subText="Provide description for the email tag."
                  isRequired={false}
                />
                <Flex flexDirection="column">
                  <TextArea
                    {...register('description')}
                    placeholder="Enter description"
                    value={watch('description') ?? ''}
                    characterLimit={100}
                  />
                  {errors.description && (
                    <FormFeedback variant="error">
                      {errors.description.message}
                    </FormFeedback>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Flex width="100%" justifyContent="flex-end" gap={4} mt={5}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={editCategoryMutation.isLoading}
                isDisabled={editCategoryMutation.isLoading}
              >
                {isCreating ? 'Create Email Tag' : 'Save Changes'}
              </Button>
            </Flex>
          </form>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default EditCategoryModal;
